import React, { useContext, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AdsContext } from '../DataContexts/AdsContext';
import { MachineContext } from '../DataContexts/MachineContext';
import { MachineGroupContext } from '../DataContexts/MachineGroupContext';
import { machineDetails, filterAndSortMachines } from './PlaylistAssign';
import DashboardSection from '../components/DashboardSection';
import BulkProgress, { useBulkRun, describeBulkSubscription } from '../components/BulkProgress';
import './PlaylistAssign.css';

const AD_IMAGE_BASE = 'https://tdj-s3.s3.eu-west-2.amazonaws.com//advertenties/';

// "Ads & Screensaver Subscriptions" section (DJB-82): the Visuals > Assign
// feature re-homed on the Subscriptions & Restrictions subpage in the modern
// ps-* look. Pick an ad on the left, then move machines between subscribed
// (middle) and available (right); groups subscribe all their machines at once.
// `active`/`onToggle` hook the section into the subpage's independent
// collapsed-section logic (DJB-83).
const AdsAssign = ({ active = true, onToggle }) => {
  const {
    advertisements,
    loading,
    error,
    addAdToMachine,
    deleteAdFromMachine,
    showSuccessNotification,
    showErrorNotification,
  } = useContext(AdsContext);
  const { machines, loading: machinesLoading, error: machinesError } = useContext(MachineContext);
  const { machineGroups, loading: machineGroupsLoading, error: machineGroupsError } = useContext(MachineGroupContext);
  const [, setSearchParams] = useSearchParams();

  const [selectedAdId, setSelectedAdId] = useState(null);
  const [adSearch, setAdSearch] = useState('');
  const [machineSearch, setMachineSearch] = useState('');
  const [machineSortKey, setMachineSortKey] = useState('name');
  const [machineSortAsc, setMachineSortAsc] = useState(true);
  const [availableMachineSearch, setAvailableMachineSearch] = useState('');
  const [availableSortKey, setAvailableSortKey] = useState('name');
  const [availableSortAsc, setAvailableSortAsc] = useState(true);
  const [groupSearch, setGroupSearch] = useState('');
  const [confirmRemoveAll, setConfirmRemoveAll] = useState(false);
  // DJB-214: one progress bar + one result toast per bulk run
  const { progress: bulkProgress, running: bulkRunning, run: runBulk } = useBulkRun();

  const selectedAd = useMemo(
    () => advertisements.find((a) => a.id === selectedAdId) ?? null,
    [advertisements, selectedAdId]
  );

  const filteredAds = useMemo(() => {
    if (!adSearch.trim()) return advertisements;
    const q = adSearch.toLowerCase().trim();
    return advertisements.filter((ad) => ad.name?.toLowerCase().includes(q));
  }, [advertisements, adSearch]);

  const subscribedMachinesWithDetails = useMemo(() => {
    if (!selectedAd?.subscribedMachines?.length) return [];
    return selectedAd.subscribedMachines.map((machineId) => {
      const machine = machines?.find((m) => m.id === machineId);
      // A subscribed machine can be missing from the machines list; keep the
      // row (with the id as its name) so it can still be unsubscribed
      return machine ? machineDetails(machine) : { id: machineId, name: machineId, place: '-', post: '-', province: '-' };
    });
  }, [selectedAd, machines]);

  const subscribedIdsSet = useMemo(
    () => new Set(selectedAd?.subscribedMachines ?? []),
    [selectedAd]
  );

  const availableMachinesWithDetails = useMemo(() => {
    if (!selectedAd || !machines?.length) return [];
    return machines.filter((m) => !subscribedIdsSet.has(m.id)).map(machineDetails);
  }, [machines, selectedAd, subscribedIdsSet]);

  const filteredAndSortedMachines = useMemo(
    () => filterAndSortMachines(subscribedMachinesWithDetails, machineSearch, machineSortKey, machineSortAsc),
    [subscribedMachinesWithDetails, machineSearch, machineSortKey, machineSortAsc]
  );

  const filteredAndSortedAvailableMachines = useMemo(
    () => filterAndSortMachines(availableMachinesWithDetails, availableMachineSearch, availableSortKey, availableSortAsc),
    [availableMachinesWithDetails, availableMachineSearch, availableSortKey, availableSortAsc]
  );

  const toggleMachineSort = (key) => {
    if (machineSortKey === key) {
      setMachineSortAsc((a) => !a);
    } else {
      setMachineSortKey(key);
      setMachineSortAsc(true);
    }
  };

  const toggleAvailableSort = (key) => {
    if (availableSortKey === key) {
      setAvailableSortAsc((a) => !a);
    } else {
      setAvailableSortKey(key);
      setAvailableSortAsc(true);
    }
  };

  const handleRemoveMachine = async (machineId) => {
    if (!selectedAdId) return;
    try {
      await deleteAdFromMachine(selectedAdId, machineId);
    } catch (e) {
      // notification already shown in context
    }
  };

  const handleAddMachine = async (machineId) => {
    if (!selectedAdId) return;
    try {
      await addAdToMachine(selectedAdId, machineId);
    } catch (e) {
      // notification already shown in context
    }
  };

  // DJB-214: bulk (un)subscribe runs one request per machine with the
  // per-machine toasts suppressed; the progress bar tracks the run and a
  // single green/red toast reports the aggregated result.
  const runBulkSubscription = async (machineIds, isSubscribing) => {
    if (!selectedAdId) return;
    const adId = selectedAdId;
    const result = await runBulk({
      label: isSubscribing ? 'Subscribing machines to ad…' : 'Unsubscribing machines from ad…',
      items: machineIds,
      action: (machineId) =>
        isSubscribing
          ? addAdToMachine(adId, machineId, { notify: false })
          : deleteAdFromMachine(adId, machineId, { notify: false }),
    });
    if (!result) return;
    const { type, message } = describeBulkSubscription(result, isSubscribing);
    if (type === 'success') {
      showSuccessNotification?.(message);
    } else {
      showErrorNotification?.(message);
    }
  };

  const handleRemoveAllMachines = () => {
    setConfirmRemoveAll(false);
    return runBulkSubscription(subscribedMachinesWithDetails.map((m) => m.id), false);
  };

  const handleAddAllMachines = () =>
    runBulkSubscription(filteredAndSortedAvailableMachines.map((m) => m.id), true);

  const filteredGroups = useMemo(() => {
    if (!machineGroups?.length) return [];
    if (!groupSearch.trim()) return machineGroups;
    const q = groupSearch.toLowerCase().trim();
    return machineGroups.filter((g) => g.name?.toLowerCase().includes(q));
  }, [machineGroups, groupSearch]);

  // skip already-subscribed members so each machine is POSTed at most once
  const handleAddGroup = (group) =>
    runBulkSubscription(
      (group.machineIds ?? []).filter((machineId) => !subscribedIdsSet.has(machineId)),
      true
    );

  // "Open in Studio" (DJB-215, formerly "Design"): deep link into Media
  // Studio > Ads & Screensaver Editor with this ad selected. The explicit tab
  // param switches the sub-tab (PlaylistHeader) and ?adId opens the editor
  // section on that ad (MediaStudioPage, DJB-81).
  const handleGoToDesigner = (adId) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', 'mediastudio');
      next.set('adId', adId);
      return next;
    });
  };

  const sortArrow = (isActive, asc) =>
    isActive ? <span className="ps-sort-arrow">{asc ? '▲' : '▼'}</span> : null;

  const sectionShell = (children) => (
    <DashboardSection
      id="section-ad-subscriptions"
      title="Ads & Screensaver Subscriptions"
      info="Abonneer machines en groepen op advertenties en screensavers."
      isOpen={active}
      onToggle={onToggle}
      bodyId="ps-body-ad-subscriptions"
    >
      {/* DJB-214: full-width progress bar above the columns while a bulk run is in flight */}
      <BulkProgress progress={bulkProgress} />
      {children}
    </DashboardSection>
  );

  if (loading || machinesLoading || machineGroupsLoading) {
    return sectionShell(<p className="ps-status">Loading data, please wait...</p>);
  }

  if (error || machinesError || machineGroupsError) {
    return sectionShell(
      <p className="ps-status ps-status--error">
        Error loading data: {error || machinesError || machineGroupsError}
      </p>
    );
  }

  return sectionShell(
    <div className="ps-columns">
      {/* First column: search and list of ads. Creating new ads lives in
          Media Studio > Ads & Screensaver Editor (DJB-89). */}
      <div className="ps-column ps-column--ads">
        <div className="ps-column-head">
          <span className="ps-column-label">Ads &amp; screensavers</span>
          <span className="ps-column-count">{filteredAds.length}</span>
        </div>
        <div className="ps-filters">
          <input
            type="text"
            className="ps-input"
            placeholder="Search by ad name..."
            value={adSearch}
            onChange={(e) => setAdSearch(e.target.value)}
          />
        </div>
        <div className="ps-list-scroll">
          <ul className="ps-playlist-list">
            {advertisements.length === 0 && <li className="ps-empty">No ads</li>}
            {advertisements.length > 0 && filteredAds.length === 0 && (
              <li className="ps-empty">No matching ads</li>
            )}
            {filteredAds.map((ad) => (
              <li
                key={ad.id}
                className={`ps-playlist-item${selectedAdId === ad.id ? ' selected' : ''}`}
                onClick={() => setSelectedAdId(ad.id)}
              >
                <div className="ps-playlist-row">
                  <span className="ps-playlist-name">{ad.name}</span>
                  <span className="ps-subs-badge">{ad.subscribedMachines?.length ?? 0} subs</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
      {/* Second column: selected ad info and subscribed machines */}
      <div className="ps-column ps-column--subscribed">
        {selectedAd ? (
          <>
            <div className="ps-selected">
              <div className="ps-selected-row">
                <div className="ps-selected-name">{selectedAd.name}</div>
                <button
                  type="button"
                  className="ps-btn ps-btn--primary"
                  title="Open this ad in the Ads & Screensaver Editor"
                  onClick={() => handleGoToDesigner(selectedAd.id)}
                >
                  Open in Studio
                </button>
              </div>
              <div className="ps-selected-id">
                Ad id: <span>{selectedAd.id}</span>
              </div>
            </div>
            {selectedAd.imageRef && (
              <div className="ps-ad-image-wrap">
                <img
                  src={`${AD_IMAGE_BASE}${selectedAd.imageRef}`}
                  alt={selectedAd.name}
                  className="ps-ad-image"
                />
              </div>
            )}
            <div className="ps-column-head">
              <h3 className="ps-column-label">Subscribed machines</h3>
              <span className="ps-column-count">{subscribedMachinesWithDetails.length}</span>
            </div>
            <div className="ps-toolbar">
              <input
                type="text"
                className="ps-input"
                placeholder="Search by name, place, post, province..."
                value={machineSearch}
                onChange={(e) => setMachineSearch(e.target.value)}
              />
              <button
                type="button"
                className="ps-btn ps-btn--danger"
                onClick={() => setConfirmRemoveAll(true)}
                disabled={subscribedMachinesWithDetails.length === 0 || bulkRunning}
              >
                Remove All
              </button>
            </div>
            {confirmRemoveAll && (
              <div className="ps-confirm-overlay">
                <div className="ps-confirm-dialog">
                  <p>Do you really want to remove all {subscribedMachinesWithDetails.length} machines from this ad?</p>
                  <div className="ps-confirm-actions">
                    <button
                      type="button"
                      className="ps-btn ps-btn--ghost"
                      onClick={() => setConfirmRemoveAll(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="ps-btn ps-btn--danger-solid"
                      onClick={handleRemoveAllMachines}
                    >
                      Yes, remove all
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div className="ps-table-scroll">
              <table className="ps-table">
                <thead>
                  <tr>
                    <th className="ps-th-sort" onClick={() => toggleMachineSort('name')}>
                      Name {sortArrow(machineSortKey === 'name', machineSortAsc)}
                    </th>
                    <th className="ps-th-sort" onClick={() => toggleMachineSort('place')}>
                      Place {sortArrow(machineSortKey === 'place', machineSortAsc)}
                    </th>
                    <th className="ps-th-sort" onClick={() => toggleMachineSort('post')}>
                      Post {sortArrow(machineSortKey === 'post', machineSortAsc)}
                    </th>
                    <th className="ps-th-sort" onClick={() => toggleMachineSort('province')}>
                      Province {sortArrow(machineSortKey === 'province', machineSortAsc)}
                    </th>
                    <th className="ps-td-action"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedMachines.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="ps-empty-cell">
                        {subscribedMachinesWithDetails.length === 0 ? 'None' : 'No matching machines'}
                      </td>
                    </tr>
                  ) : (
                    filteredAndSortedMachines.map((m) => (
                      <tr key={m.id}>
                        <td>{m.name}</td>
                        <td>{m.place}</td>
                        <td>{m.post}</td>
                        <td>{m.province}</td>
                        <td className="ps-td-action">
                          <button
                            className="ps-icon-btn ps-icon-btn--remove"
                            title="Remove from ad"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveMachine(m.id);
                            }}
                          >
                            -
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="ps-placeholder">Select an ad to see details</div>
        )}
      </div>
      {/* Third column: available machines and machine groups */}
      <div className="ps-column ps-column--available">
        {!selectedAd ? (
          <div className="ps-placeholder">Select an ad to see details</div>
        ) : (
          <>
            <div className="ps-column-head">
              <h3 className="ps-column-label">Available machines</h3>
              <span className="ps-column-count">{availableMachinesWithDetails.length}</span>
            </div>
            <div className="ps-toolbar">
              <input
                type="text"
                className="ps-input"
                placeholder="Search by name, place, post, province..."
                value={availableMachineSearch}
                onChange={(e) => setAvailableMachineSearch(e.target.value)}
              />
              <button
                type="button"
                className="ps-btn ps-btn--success"
                title="Add all to ad"
                onClick={handleAddAllMachines}
                disabled={filteredAndSortedAvailableMachines.length === 0 || bulkRunning}
              >
                Add All
              </button>
            </div>
            <div className="ps-table-scroll ps-table-scroll--available">
              <table className="ps-table">
                <thead>
                  <tr>
                    <th className="ps-th-sort" onClick={() => toggleAvailableSort('name')}>
                      Name {sortArrow(availableSortKey === 'name', availableSortAsc)}
                    </th>
                    <th className="ps-th-sort" onClick={() => toggleAvailableSort('place')}>
                      Place {sortArrow(availableSortKey === 'place', availableSortAsc)}
                    </th>
                    <th className="ps-th-sort" onClick={() => toggleAvailableSort('post')}>
                      Post {sortArrow(availableSortKey === 'post', availableSortAsc)}
                    </th>
                    <th className="ps-th-sort" onClick={() => toggleAvailableSort('province')}>
                      Province {sortArrow(availableSortKey === 'province', availableSortAsc)}
                    </th>
                    <th className="ps-td-action"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedAvailableMachines.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="ps-empty-cell">
                        {availableMachinesWithDetails.length === 0
                          ? 'All machines subscribed'
                          : 'No matching machines'}
                      </td>
                    </tr>
                  ) : (
                    filteredAndSortedAvailableMachines.map((m) => (
                      <tr key={m.id}>
                        <td>{m.name}</td>
                        <td>{m.place}</td>
                        <td>{m.post}</td>
                        <td>{m.province}</td>
                        <td className="ps-td-action">
                          <button
                            className="ps-icon-btn ps-icon-btn--add"
                            title="Add to ad"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddMachine(m.id);
                            }}
                          >
                            +
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="ps-column-head ps-column-head--groups">
              <h3 className="ps-column-label">Machine groups</h3>
              <span className="ps-column-count">{filteredGroups.length}</span>
            </div>
            <div className="ps-toolbar">
              <input
                type="text"
                className="ps-input"
                placeholder="Search by group name..."
                value={groupSearch}
                onChange={(e) => setGroupSearch(e.target.value)}
              />
            </div>
            <div className="ps-table-scroll ps-table-scroll--groups">
              <table className="ps-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Machines</th>
                    <th className="ps-td-action"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGroups.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="ps-empty-cell">
                        {!machineGroups?.length ? 'No groups' : 'No matching groups'}
                      </td>
                    </tr>
                  ) : (
                    filteredGroups.map((group) => (
                      <tr key={group.id}>
                        <td>{group.name}</td>
                        <td>{group.machineIds?.length ?? 0}</td>
                        <td className="ps-td-action">
                          <button
                            className="ps-icon-btn ps-icon-btn--add"
                            title="Add all machines in this group to ad"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddGroup(group);
                            }}
                            disabled={!group.machineIds?.length || bulkRunning}
                          >
                            +
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdsAssign;
